<script lang="ts">
  import { onMount, onDestroy } from 'svelte';
  import { weightsStore } from '$lib/stores/weightsStore';
  import { goto } from '$app/navigation';
  import { routes } from '$lib/routes';
  import { fade } from 'svelte/transition';
  import { browser } from '$app/environment';
  import type { 
    AxisDefinition, 
    ProfileDefinition, 
    WeightsState 
  } from '$lib/stores/weightsStore';
  import { surveyStore } from '$lib/stores/surveyStore';

  // Props
  export let hidden = false;
  export let forceVisible: boolean = false;

  // Local state
  let isOpen = false;
  let isSmallScreen = false;
  let weightsData: WeightsState | null = null;
  let unsubscribe: () => void;
  let surveyUnsubscribe: () => void;
  let previewMode = false;
  let previousProfileId: string | null = null;
  let navigatorPanel: HTMLElement;
  
  // Add state for axis selection
  let selectedXAxisIndex = 0;
  let selectedYAxisIndex = 1;
  let showAxisSelector = false;
  
  // Define a set of pastel colors for profiles
  const profileColors = [
    '#FFB6C1', // Light Pink
    '#FFD700', // Gold
    '#98FB98', // Pale Green
    '#ADD8E6', // Light Blue
    '#E6A8D7', // Light Purple
    '#FFDAB9', // Peach
    '#87CEFA', // Light Sky Blue
    '#FFA07A', // Light Salmon
    '#FFFACD', // Lemon Chiffon
    '#B0E0E6'  // Powder Blue
  ];
  
  // Check for preview mode in URL on initialization
  if (browser) {
    const url = new URL(window.location.href);
    previewMode = url.searchParams.get('preview') === 'true';
  }
  
  // Set up scroll handling
  let isManuallyScrolling = false;
  let scrollTimeout: ReturnType<typeof setTimeout> | null = null;

  // Function to get a profile color by index
  function getProfileColor(index: number): string {
    return profileColors[index % profileColors.length];
  }

  // Function to get all possible axis combinations
  function getAxisCombinations() {
    const axes = getSafeAxes();
    if (axes.length < 2) return [];
    
    const combinations = [];
    for (let i = 0; i < axes.length - 1; i++) {
      for (let j = i + 1; j < axes.length; j++) {
        combinations.push({
          xIndex: i,
          yIndex: j,
          label: `${axes[i].id} × ${axes[j].id}`
        });
      }
    }
    return combinations;
  }
  
  // Function to check if an axis is currently displayed in the grid
  function isAxisInGrid(axisId: string): boolean {
    const axes = getSafeAxes();
    if (axes.length < 2) return false;
    
    const xAxisId = axes[selectedXAxisIndex]?.id;
    const yAxisId = axes[selectedYAxisIndex]?.id;
    
    return axisId === xAxisId || axisId === yAxisId;
  }

  // Function to safely access weightsData profiles
  function getSafeProfiles() {
    return weightsData?.profiles || [];
  }

  // Function to safely access weightsData axes
  function getSafeAxes() {
    return weightsData?.axes || [];
  }
  
  // Function to calculate each profile's area in the grid
  function calculateProfilePositions() {
    const profiles = getSafeProfiles();
    const axes = getSafeAxes();
    
    if (axes.length < 2 || profiles.length === 0) return [];
    
    // Use the selected axes for the grid visualization
    const xAxis = axes[selectedXAxisIndex] || axes[0];
    const yAxis = axes[selectedYAxisIndex] || axes[1];
    
    const positions = profiles.map((profile, index) => {
      // Find conditions for each axis
      const xCondition = profile.conditions?.find(c => c.axis === xAxis.id);
      const yCondition = profile.conditions?.find(c => c.axis === yAxis.id);
      
      // Default to full range if no conditions (axes are already normalized to 0-100)
      let xMin = 0;
      let xMax = 100;
      let yMin = 0;
      let yMax = 100;
      
      // Apply X conditions if they exist
      if (xCondition) {
        if (xCondition.min !== undefined) xMin = xCondition.min;
        if (xCondition.max !== undefined) xMax = xCondition.max;
      }
      
      // Apply Y conditions if they exist
      if (yCondition) {
        if (yCondition.min !== undefined) yMin = yCondition.min;
        if (yCondition.max !== undefined) yMax = yCondition.max;
      }
      
      // Simple direct calculations
      const left = xMin;
      const top = 100 - yMax; // Invert Y because CSS has 0 at top
      const width = xMax - xMin;
      const height = yMax - yMin;
      
      return {
        profile,
        color: getProfileColor(index),
        left,
        top,
        width,
        height
      };
    });
    
    return positions;
  }
  
  // Function to safely access current profile
  function getCurrentProfile() {
    if (!weightsData || !weightsData.assignedProfileId) return null;
    return getSafeProfiles().find(p => p.id === weightsData!.assignedProfileId);
  }
  
  // Force immediate reactivity when scores change
  $: if (weightsData && weightsData.normalizedScores) {
    // Reactive update to scores
  }

  // Function to safely get normalized score - now reactive
  function getNormalizedScore(axisId: string): number {
    // Using reactive access pattern
    return $weightsStore?.normalizedScores?.[axisId] ?? 0;
  }

  // Function to safely get assigned profile ID
  function getAssignedProfileId(): string | null {
    // Using reactive access pattern
    return $weightsStore?.assignedProfileId ?? null;
  }

  // Check if a given profile is the currently assigned one
  function isAssignedProfile(profileId: string): boolean {
    return getAssignedProfileId() === profileId;
  }

  // Handle subscription to weightsStore
  function setupWeightsSubscription() {
    // Clean up any existing subscription
    if (unsubscribe) unsubscribe();
    
    // Create new subscription
    unsubscribe = weightsStore.subscribe(state => {
      // Force reactivity by creating a new object
      weightsData = state ? { ...state } : null;
      
      // Check if profile changed
      if (state?.assignedProfileId !== previousProfileId) {
        previousProfileId = state?.assignedProfileId || null;
        
        // Highlight the newly active profile
        if (state?.assignedProfileId && navigatorPanel) {
          setTimeout(() => {
            const activeProfile = navigatorPanel.querySelector('.profile-item.active');
            if (activeProfile) {
              activeProfile.classList.add('highlight-pulse');
              setTimeout(() => {
                activeProfile.classList.remove('highlight-pulse');
              }, 1500);
            }
          }, 100);
        }
      }
    });
  }
  
  // onMount handler
  onMount(() => {
    // Set up the weights subscription
    setupWeightsSubscription();
    
    // Check if we're in preview mode using the survey store
    surveyUnsubscribe = surveyStore.subscribe(state => {
      previewMode = !!state?.previewMode;
    });
    
    // Check screen size
    checkScreenSize();
    
    // Add resize listener
    if (browser) {
      window.addEventListener('resize', checkScreenSize);
      
      // Set up scroll handlers
      setTimeout(() => {
        if (navigatorPanel) {
          navigatorPanel.addEventListener('scroll', handleManualScroll);
        }
      }, 100);
    }
  });
  
  onDestroy(() => {
    if (unsubscribe) unsubscribe();
    if (surveyUnsubscribe) surveyUnsubscribe();
    
    // Remove listeners
    if (browser) {
      window.removeEventListener('resize', checkScreenSize);
      if (navigatorPanel) {
        navigatorPanel.removeEventListener('scroll', handleManualScroll);
      }
      if (scrollTimeout) {
        clearTimeout(scrollTimeout);
      }
    }
  });
  
  // Handle manual scrolling
  function handleManualScroll() {
    isManuallyScrolling = true;
    
    // Reset the manual scrolling flag after a delay
    if (scrollTimeout) clearTimeout(scrollTimeout);
    scrollTimeout = setTimeout(() => {
      isManuallyScrolling = false;
    }, 1000); // Reset after 1 second of no scrolling
  }
  
  // Check screen size
  function checkScreenSize() {
    if (browser) {
      isSmallScreen = window.innerWidth < 1024;
      
      // Automatically open navigator on large screens
      if (!isSmallScreen) {
        isOpen = true;
      }
    }
  }
  
  // Toggle the navigator open/closed
  function toggleNavigator() {
    isOpen = !isOpen;
  }
  
  // Navigate to a profile
  function navigateToProfile(profileId: string) {
    if (!browser || !profileId) return;
    goto(routes.profile.withId(profileId), { replaceState: false });
  }
  
  // Get a color class based on the normalized score
  function getScoreColorClass(score: number): string {
    if (score < 33) return 'score-low';
    if (score < 66) return 'score-medium';
    return 'score-high';
  }
  
  // Format a score for display
  function formatScore(score: number): string {
    return score.toFixed(0);
  }
  
  // Reset the weights store
  function resetState() {
    if (confirm('Are you sure you want to reset all profile data? This will clear all your answers.')) {
      weightsStore.reset();
      // Also navigate back to the first question
      goto(routes.survey.start());
    }
  }

  // Toggle axis selector visibility
  function toggleAxisSelector() {
    showAxisSelector = !showAxisSelector;
  }
  
  // Handle axis combination selection
  function handleAxisSelection(e: Event) {
    const select = e.target as HTMLSelectElement;
    const value = select.value;
    if (value === 'default') return; // Skip handling default option
    
    const [xIndex, yIndex] = value.split('-').map(Number);
    selectedXAxisIndex = xIndex;
    selectedYAxisIndex = yIndex;
  }
</script>

{#if previewMode || forceVisible}
  <div
    class="profile-navigator"
    class:open={isOpen}
    class:small-screen={isSmallScreen}
    class:large-screen={!isSmallScreen}
    class:force-hidden={hidden}
    transition:fade={{ duration: 200, delay: 50 }}
  >
    <button
      class="toggle-button right"
      on:click={toggleNavigator}
      aria-label={isOpen ? 'Close profile navigator' : 'Open profile navigator'}
    >
      {#if isOpen}
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
          <line x1="18" y1="6" x2="6" y2="18"></line>
          <line x1="6" y1="6" x2="18" y2="18"></line>
        </svg>
      {:else}
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
          <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
          <circle cx="12" cy="7" r="4"></circle>
        </svg>
      {/if}
    </button>
    
    {#if isOpen}
      <div class="navigator-panel" bind:this={navigatorPanel}>
        <div class="header">
          <div class="header-content">
            <h3>Profile Navigator</h3>
          </div>
          
          {#if !$weightsStore}
            <div class="loading-indicator">Initializing...</div>
          {/if}
        </div>
        
        {#if $weightsStore}
          <div class="combined-content">
           <!-- Axes List -->
           {#if getSafeAxes().length > 0}
           <ul class="axes-list">
             {#each getSafeAxes() as axis, axisIndex}
               {@const normalizedScore = getNormalizedScore(axis.id)}
               <li class="axis-item" class:dimmed={!isAxisInGrid(axis.id)}>
                 <div class="axis-header">
                   <h3 class="axis-name">{axis.id}</h3>
                   <div class="axis-score {getScoreColorClass(normalizedScore)}">
                     {formatScore(normalizedScore)}
                   </div>
                 </div>
                 
                 <div class="axis-progress">
                   <div class="progress-bar">
                     <div 
                       class="progress-fill {getScoreColorClass(normalizedScore)}" 
                       style="width: {normalizedScore}%;"
                     ></div>
                   </div>
                 </div>
               </li>
             {/each}
           </ul>
         {/if}
          

            <!-- Profile Coverage Visualization -->
            <div class="profile-coverage">
              <div class="coverage-visualization">
                {#if getSafeAxes().length >= 2 && getSafeProfiles().length > 0}
                  <!-- 2D representation for axes -->
                  <div class="coverage-grid">
                    <div class="grid-backdrop">
                      <div class="grid-line horizontal-line top"></div>
                      <div class="grid-line horizontal-line middle"></div>
                      <div class="grid-line horizontal-line bottom"></div>
                      <div class="grid-line vertical-line left"></div>
                      <div class="grid-line vertical-line center"></div>
                      <div class="grid-line vertical-line right"></div>
                    </div>
                    {#if getSafeAxes().length >= 2}
                      <!-- Create the grid visualization -->
                      <div class="axis-labels">
                        <div class="coverage-axis-label x-axis">
                          {getSafeAxes()[selectedXAxisIndex]?.id || ''}
                        </div>
                        <div class="coverage-axis-label y-axis">
                          {getSafeAxes()[selectedYAxisIndex]?.id || ''}
                        </div>
                        
                        <!-- Axis scale markers -->
                        <div class="axis-marker x-min">0</div>
                        <div class="axis-marker x-mid">50</div>
                        <div class="axis-marker x-max">100</div>
                        <div class="axis-marker y-min">0</div>
                        <div class="axis-marker y-mid">50</div>
                        <div class="axis-marker y-max">100</div>
                      </div>
                      
                      <!-- Profile areas mapped to the grid -->
                      <div class="profile-areas">
                        {#each calculateProfilePositions() as position}
                          <div 
                            class="profile-area" 
                            style="
                              background-color: {position.color};
                              left: {position.left}%;
                              top: {position.top}%;
                              width: {position.width}%;
                              height: {position.height}%;
                            "
                            class:active={isAssignedProfile(position.profile.id)}
                            on:click={() => navigateToProfile(position.profile.id)}
                            on:keydown={(e) => e.key === 'Enter' && navigateToProfile(position.profile.id)}
                            tabindex="0"
                          ></div>
                        {/each}
                      </div>
                    {/if}
                  </div>
                  
                {:else}
                  <!-- For cases with more than 2 axes or other configurations -->
                  <p class="coverage-note">Profile coverage visualization for {getSafeAxes().length} axes and {getSafeProfiles().length} profiles.</p>
                {/if}
              </div>
              <!-- Axis Selector - Only show if we have more than 2 axes -->
              {#if getSafeAxes().length > 2}
                <div class="axis-selector-container">
                  <select 
                    class="axis-select"
                    on:change={handleAxisSelection}
                    title="Select axes for grid"
                    aria-label="Select axes for grid"
                  >
                    <option value="default" disabled selected={selectedXAxisIndex === -1}>Select the active axes</option>
                    {#each getAxisCombinations() as combo}
                      <option 
                        value="{combo.xIndex}-{combo.yIndex}" 
                        selected={selectedXAxisIndex === combo.xIndex && selectedYAxisIndex === combo.yIndex}
                      >
                        {combo.label}
                      </option>
                    {/each}
                  </select>
                </div>
              {/if}
              

            </div>
           
         
            <!-- Profiles List -->
            {#if getSafeProfiles().length > 0}
              <div class="section-title">Profiles</div>
              <ul class="profiles-list">
                {#each getSafeProfiles() as profile, i}
                  <li 
                    class="profile-item" 
                    class:active={isAssignedProfile(profile.id)}
                    on:click={() => navigateToProfile(profile.id)}
                    on:keydown={(e) => e.key === 'Enter' && navigateToProfile(profile.id)}
                    tabindex="0"
                    role="button"
                    style="border-left-color: {getProfileColor(i)};"
                  >
                    <div class="profile-header">
                      <h3 class="profile-name">{profile.label || profile.id}</h3>
                      {#if isAssignedProfile(profile.id)}
                        <div class="assigned-badge">Current</div>
                      {/if}
                    </div>
                    
                    {#if profile.conditions && profile.conditions.length > 0}
                      <ul class="conditions-list">
                        {#each profile.conditions as condition}
                          <li class="condition-item">
                            <span class="condition-axis">{condition.axis}</span>
                            {#if condition.min !== undefined && condition.max !== undefined}
                              <span class="condition-range">
                                {condition.min} to {condition.max}
                              </span>
                            {:else if condition.min !== undefined}
                              <span class="condition-range">
                                ≥ {condition.min}
                              </span>
                            {:else if condition.max !== undefined}
                              <span class="condition-range">
                                ≤ {condition.max}
                              </span>
                            {/if}
                          </li>
                        {/each}
                      </ul>
                    {/if}
                  </li>
                {/each}
              </ul>
            {/if}
          </div>
          
          <!-- Reset Button at bottom -->
          <div class="footer-actions">
            <button class="reset-button" on:click={resetState}>
              Reset All Data
            </button>
          </div>
        {/if}
      </div>
    {/if}
  </div>
{/if}

<style>
  .profile-navigator {
    position: fixed;
    z-index: 1000;
    font-family: var(--theme-font-family, system-ui, sans-serif);
    user-select: none;
  }
  
  /* Apply theme font to all elements */
  .profile-navigator * {
    font-family: var(--theme-font-family, system-ui, sans-serif);
  }
  
  /* Small screen styles (mobile, tablet) */
  .profile-navigator.small-screen {
    top: 5px;
    right: 46px;
    display: flex;
    flex-direction: column;
    align-items: flex-end;
  }
  
  /* Large screen styles (laptop and above) */
  .profile-navigator.large-screen {
    top: 0;
    right: 0;
    height: 100vh;
    display: flex;
    justify-content: flex-end;
  }
  
  .toggle-button {
    width: 36px;
    height: 36px;
    border-radius: var(--radius, 5px);
    background: var(--theme-primary-color, #10b981);
    color: var(--theme-text-on-primary, white);
    border: none;
    font-size: 18px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
    transition: all 0.2s ease;
    z-index: 1002;
  }
  
  .toggle-button.right {
    right: 0;
  }
  
  .toggle-button:hover {
    transform: scale(1.05);
  }

  .toggle-button svg {
    pointer-events: none;
  }

  .large-screen .toggle-button {
    display: none;
  }
  
  .navigator-panel {
    background: white;
    border-radius: 8px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    max-height: 100vh;
  }
  
  .small-screen .navigator-panel {
    margin-top: 10px;
    width: 350px;
    max-height: 70vh;
    animation: slideIn 0.2s ease;
  }
  
  .large-screen .navigator-panel {
    height: 100vh;
    width: 350px;
    border-radius: 0;
    border-left: 1px solid var(--theme-border, #e0e0e0);
  }
  
  .header {
    padding: 15px;
    border-bottom: 1px solid #eaeaea;
    position: sticky;
    top: 0;
    background: white;
    z-index: 2;
  }
  
  .header-content {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
  }
  
  .header h3 {
    margin: 0;
    font-size: 16px;
    color: var(--theme-text-color, #1f2937);
    font-weight: 600;
    text-align: left;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  
  .loading-indicator {
    color: var(--theme-secondary-color, #6b7280);
    font-size: 12px;
    font-style: italic;
    margin-top: 4px;
  }
  
  /* Axis Selector Styles */
  .axis-selector-container {
    margin-top: 24px;
    padding-top: 2rem;
  }
  
  .axis-select {
    font-size: 12px;
    padding: 4px 8px;
    border-radius: 4px;
    border: 1px solid #ddd;
    background-color: white;
    color: var(--theme-text-color, #1f2937);
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
    width: 100%;
  }
  
  .axis-select:focus {
    outline: none;
    border-color: var(--theme-primary-color, #10b981);
    box-shadow: 0 0 0 1px var(--theme-primary-color, #10b981);
  }
  
  .combined-content {
    flex: 1;
    overflow-y: auto;
    padding-bottom: 15px;
  }
  
  .section-title {
    font-weight: 600;
    font-size: 16px;
    padding: 10px 15px;
    color: var(--theme-text-color, #1f2937);
    background-color: #f8f9fa;
    border-bottom: 1px solid #eaeaea;
    margin-top: 10px;
  }
  
  .axes-list, .profiles-list {
    list-style: none;
    padding: 0;
    margin: 0;
  }
  
  .axis-item {
    display: flex;
    flex-direction: column;
    padding: 6px 15px;
    border-bottom: 1px solid #f0f0f0;
    outline: none;
  }
  
  .profile-item {
    display: flex;
    flex-direction: column;
    padding: 12px 15px;
    border-bottom: 1px solid #f0f0f0;
    cursor: pointer;
    transition: background-color 0.2s ease;
    outline: none;
    border-left: 6px solid transparent;
  }
  
  .profile-item:hover {
    background-color: rgba(16, 185, 129, 0.05);
  }
  
  .profile-item:focus {
    background-color: rgba(16, 185, 129, 0.05);
    box-shadow: 0 0 0 2px var(--theme-primary-color, #10b981) inset;
  }
  
  .profile-item.active {
    background-color: rgba(16, 185, 129, 0.1);
  }
  
  .axis-header, .profile-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 6px;
  }
  
  .axis-name, .profile-name {
    margin: 0;
    font-size: 16px;
    font-weight: 600;
    color: var(--theme-text-color, #1f2937);
  }
  
  .axis-score {
    font-weight: bold;
    padding: 4px 8px;
    border-radius: 12px;
    font-size: 14px;
    min-width: 40px;
    text-align: center;
  }
  
  .score-low {
    background-color: #fee2e2;
    color: #b91c1c;
  }
  
  .score-medium {
    background-color: #fef3c7;
    color: #92400e;
  }
  
  .score-high {
    background-color: #d1fae5;
    color: #065f46;
  }
  
  .axis-progress {
    margin-bottom: 6px;
  }
  
  .progress-bar {
    height: 8px;
    background-color: #e5e7eb;
    border-radius: 4px;
    overflow: hidden;
    margin-bottom: 5px;
  }
  
  .progress-fill {
    height: 100%;
    border-radius: 4px;
  }
  
  .assigned-badge {
    background-color: var(--theme-primary-color, #10b981);
    color: white;
    padding: 2px 8px;
    border-radius: 12px;
    font-size: 12px;
  }
  
  .conditions-list {
    padding: 0;
    margin: 0;
    list-style: none;
  }
  
  .condition-item {
    display: flex;
    justify-content: space-between;
    font-size: 13px;
    margin-bottom: 3px;
    color: var(--theme-secondary-color, #6b7280);
  }
  
  .condition-axis {
    font-weight: 500;
  }
  
  .condition-range {
    color: var(--theme-secondary-color, #6b7280);
  }
  
  .empty-state {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 200px;
    color: var(--theme-text-color, #1f2937);
    font-style: italic;
  }
  
  @keyframes slideIn {
    from {
      opacity: 0;
      transform: translateY(-10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
  
  .current-profile-info {
    padding: 15px;
    border-bottom: 1px solid #f0f0f0;
    background-color: rgba(16, 185, 129, 0.05);
  }
  
  .current-profile-name {
    margin: 0;
    font-size: 18px;
    font-weight: 700;
    color: var(--theme-text-color, #1f2937);
    text-align: center;
  }
  
  .footer-actions {
    padding: 15px;
    border-top: 1px solid #eaeaea;
    display: flex;
    justify-content: center;
  }
  
  .reset-button {
    background-color: #ef4444;
    color: white;
    border: none;
    padding: 8px 16px;
    border-radius: 4px;
    font-size: 14px;
    cursor: pointer;
    transition: background-color 0.2s ease;
  }
  
  .reset-button:hover {
    background-color: #dc2626;
  }
  
  .profile-coverage {
    padding: 0 15px;
    border-bottom: 1px solid #f0f0f0;
    margin-bottom: 10px;
  }
  
  .coverage-title {
    font-size: 16px;
    font-weight: 600;
    margin: 0 0 15px 0;
    color: var(--theme-text-color, #1f2937);
    text-align: center;
  }
  
  .coverage-visualization {
    margin-bottom: 15px;
  }
  
  .coverage-grid {
    width: 280px;
    height: 280px;
    position: relative;
    margin: 30px auto 20px;
    background: none;
    padding: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    box-sizing: border-box;
  }
  
  .axis-labels {
    position: absolute;
    width: 100%;
    height: 100%;
    pointer-events: none;
    z-index: 5;
  }
  
  .coverage-axis-label {
    position: absolute;
    font-size: 13px;
    font-weight: 600;
    color: var(--theme-text-color, #1f2937);
    z-index: 5;
  }
  
  .coverage-axis-label.x-axis {
    bottom: -33px;
    left: 50%;
    transform: translateX(-50%);
    text-align: center;
  }
  
  .coverage-axis-label.y-axis {
    left: -78px;
    top: 50%;
    transform: translateY(-50%) rotate(-90deg);
    text-align: center;
    width: 100px;
  }
  
  .axis-marker {
    position: absolute;
    font-size: 11px;
    color: #666;
  }
  
  .axis-marker.x-min {
    bottom: -20px;
    left: 0;
  }
  
  .axis-marker.x-mid {
    bottom: -20px;
    left: 50%;
    transform: translateX(-50%);
  }
  
  .axis-marker.x-max {
    bottom: -20px;
    right: 0;
  }
  
  .axis-marker.y-min {
    left: -20px;
    bottom: 0;
  }
  
  .axis-marker.y-mid {
    left: -20px;
    top: 50%;
    transform: translateY(-50%);
  }
  
  .axis-marker.y-max {
    left: -20px;
    top: 0;
  }
  
  .grid-backdrop {
    position: absolute;
    width: 100%;
    height: 100%;
    border: 1px solid #ddd;
    pointer-events: none;
    background-color: rgba(0,0,0,0.01);
  }
  
  .grid-line {
    position: absolute;
    background-color: rgba(0,0,0,0.06);
  }
  
  .horizontal-line {
    width: 100%;
    height: 1px;
  }
  
  .horizontal-line.top {
    top: 33.33%;
  }
  
  .horizontal-line.middle {
    top: 66.66%;
  }
  
  .horizontal-line.bottom {
    bottom: 0;
  }
  
  .vertical-line {
    height: 100%;
    width: 1px;
  }
  
  .vertical-line.left {
    left: 33.33%;
  }
  
  .vertical-line.center {
    left: 66.66%;
  }
  
  .vertical-line.right {
    right: 0;
  }
  
  .profile-dot {
    position: absolute;
    width: 16px;
    height: 16px;
    border-radius: 50%;
    transform: translate(-50%, -50%);
    box-shadow: 0 0 0 2px white, 0 0 0 3px var(--profile-dot-border, rgba(0, 0, 0, 0.2));
    transition: all 0.3s ease-out;
  }
  
  .axis-container {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    pointer-events: none;
    z-index: 2;
  }
  
  .axis {
    position: absolute;
    width: calc(100% - 20px);
    height: calc(100% - 20px);
    top: 10px;
    left: 10px;
  }
  
  .axis-line {
    position: absolute;
    background-color: rgba(0, 0, 0, 0.15);
  }
  
  .axis-x {
    height: 1.5px;
    width: 100%;
    top: 50%;
    transform: translateY(-50%);
  }
  
  .axis-y {
    width: 1.5px;
    height: 100%;
    left: 50%;
    transform: translateX(-50%);
  }
  
  .marker-top {
    top: -18px;
    left: 50%;
    transform: translateX(-50%);
    text-align: center;
  }
  
  .marker-right {
    right: -22px;
    top: 50%;
    transform: translateY(-50%);
    text-align: right;
  }
  
  .marker-bottom {
    bottom: -18px;
    left: 50%;
    transform: translateX(-50%);
    text-align: center;
  }
  
  .marker-left {
    left: -22px;
    top: 50%;
    transform: translateY(-50%);
    text-align: left;
  }
  
  .threshold-container {
    position: absolute;
    width: calc(100% - 40px);
    height: calc(100% - 40px);
    top: 20px;
    left: 20px;
    pointer-events: none;
  }
  
  .threshold-line {
    position: absolute;
    background-color: rgba(0, 0, 0, 0.06);
  }
  
  @media (max-width: 768px) {
    .toggle-button {
      padding: 6px;
    }
    
    .toggle-button span {
      display: none;
    }
  }
  
  .coverage-grid, .scores-list {
    max-width: 100%;
  }
  
  /* Dark mode support */
  :global([data-theme='dark']) .navigator-panel,
  :global([data-theme='dark']) .header {
    background-color: var(--theme-bg-color-dark, #1f2937);
    border-color: var(--theme-border-color-dark, #374151);
  }
  
  :global([data-theme='dark']) .legend-item.active {
    box-shadow: 0 0 0 1px #1f2937, 0 0 0 2px var(--theme-primary-color, #10b981), 0 2px 5px rgba(0,0,0,0.4);
  }
  
  :global([data-theme='dark']) .coverage-note {
    color: var(--theme-secondary-color, #9ca3af);
  }
  
  :global([data-theme='dark']) .profile-item.active,
  :global([data-theme='dark']) .profile-item.highlight-pulse {
    background-color: rgba(16, 185, 129, 0.2);
  }
  
  .profile-area {
    position: absolute;
    opacity: 0.7;
    cursor: pointer;
    transition: all 0.2s ease;
    border-radius: 4px;
    box-shadow: 0 0 2px rgba(0,0,0,0.1);
  }
  
  .profile-area:hover, .profile-area:focus {
    opacity: 0.9;
    transform: scale(1.01);
    z-index: 5;
    box-shadow: 0 0 6px rgba(0,0,0,0.2);
  }
  
  .profile-area.active {
    opacity: 0.9;
    z-index: 4;
    box-shadow: 0 0 0 2px white, 0 0 0 3px var(--theme-primary-color, #10b981);
  }
  
  .profile-areas {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: 3;
  }
  
  .threshold-container {
    position: absolute;
    width: calc(100% - 20px);
    height: calc(100% - 20px);
    top: 10px;
    left: 10px;
    pointer-events: none;
    z-index: 2;
  }
  
  .threshold-line {
    position: absolute;
    background-color: rgba(0, 0, 0, 0.08);
  }
  
  /* Dimmed axis style */
  .axis-item.dimmed {
    opacity: 0.5;
  }

  /* Force hidden when master toggle is activated */
  .profile-navigator.force-hidden {
    display: none !important;
    visibility: hidden !important;
    opacity: 0 !important;
    pointer-events: none !important;
  }
</style> 